import { getStoredJSON, toCamel } from "@workspace/shared";
import { clearImpersonation } from "@workspace/shared/hooks";

import { apiUrl } from "./apiUrl";
import {
	runAfterFetch,
	runBeforeFetch,
	type InterceptedRequest,
} from "./fetchInterceptors";

export interface ApiError {
	success: false;
	error: string;
	details: Record<string, never> & {
		errors?: Record<string, string[]>;
	};
	/** HA-804: the HTTP status when the server answered; absent for a dropped connection. */
	status?: number;
	/** HA-804: the problem's `code`, or NETWORK_ERROR_CODE when no response came back. */
	code?: string;
}

export interface ApiResponse<T> {
	success: true;
	response?: T;
}

export type ApiResult<T> = ApiResponse<T> | ApiError;

// Token management
let tokenGetter: (() => string | null | undefined) | null = null;

export function setAuthTokenGetter(
	getter: () => string | null | undefined,
): void {
	tokenGetter = getter;
}

// HA-831: tokens that apply only to some requests (the EPOS staff session's, on the EPOS routes). HA-649 Slice 0: the Employee
// Centre's session is another, so there is a list.
const scopedTokenProviders: Array<(url: string) => string | null | undefined> =
	[];

/**
 * HA-831 / HA-649 Slice 0 — registers a token for the requests it recognises by URL, ahead of the app's own token. The EPOS staff
 * session and the Employee Centre session each register one: each token is sent on its own routes only, and the till's own token on
 * everything else. The first provider with a token for the URL wins; admin impersonation still comes first. Returns the unregister.
 */
export function addScopedAuthTokenProvider(
	provider: (url: string) => string | null | undefined,
): () => void {
	scopedTokenProviders.push(provider);
	return () => {
		const index = scopedTokenProviders.indexOf(provider);
		if (index >= 0) scopedTokenProviders.splice(index, 1);
	};
}

export function getAuthToken(url?: string): string | null | undefined {
	// Impersonation token takes precedence (per-tab via sessionStorage)
	if (typeof window !== "undefined") {
		const raw = sessionStorage.getItem("impersonation_token");
		if (raw) {
			try {
				const { token, expires } = JSON.parse(raw);
				if (new Date(expires) > new Date()) return token;
				clearImpersonation();
			} catch {
				clearImpersonation();
			}
		}
	}

	if (url) {
		for (const provider of scopedTokenProviders) {
			const scoped = provider(url);
			if (scoped) return scoped;
		}
	}

	if (tokenGetter) {
		return tokenGetter();
	}

	// Fallback: try to get token from common storage locations
	if (typeof window !== "undefined") {
		// Try localStorage first
		const storedToken = getStoredJSON<{ token: string }>("token");
		if (storedToken) {
			return storedToken.token;
		}
	}

	return null;
}

// RFC 7807 ProblemDetails shape returned by ASP.NET endpoints on 4xx/5xx.
// `title` is typically a code we use to distinguish error variants (e.g.
// `DUPLICATE_ACTIVITY` vs `FACILITY_ACTIVITY_EXISTS`); `detail` is human-readable.
export interface ProblemDetails {
	type?: string;
	title?: string;
	status?: number;
	detail?: string;
	instance?: string;
	[key: string]: unknown;
}

export class HttpRequestError extends Error {
	response: Response;
	problem?: ProblemDetails;
	constructor(message: string, response: Response, problem?: ProblemDetails) {
		super(message);
		this.name = "HttpRequestError";
		this.response = response;
		this.problem = problem;
	}
}

export function isHttpStatus(
	error: unknown,
	status: number,
): error is HttpRequestError {
	return error instanceof HttpRequestError && error.response.status === status;
}

export function isHttpNotFound(error: unknown): error is HttpRequestError {
	return isHttpStatus(error, 404);
}

/** HA-816: the API's answer to any request for a tenant the caller is suspended in. */
export const TENANT_MEMBERSHIP_SUSPENDED_CODE = "TENANT_MEMBERSHIP_SUSPENDED";

/** HA-869: the API's answer to a request for a tenant the caller doesn't belong to. */
export const NOT_A_TENANT_MEMBER_CODE = "NOT_A_TENANT_MEMBER";

export function isTenantSuspended(error: unknown): error is HttpRequestError {
	return (
		isHttpStatus(error, 403) &&
		error.problem?.code === TENANT_MEMBERSHIP_SUSPENDED_CODE
	);
}

export function isNotATenantMember(error: unknown): error is HttpRequestError {
	return (
		isHttpStatus(error, 403) && error.problem?.code === NOT_A_TENANT_MEMBER_CODE
	);
}

let tenantSuspendedHandler: (() => void) | null = null;

/**
 * HA-816 — what the app does when a request is refused because the caller is suspended in the
 * current tenant: the tenant context drops that tenant and reloads, so they land on another one.
 */
export function setTenantSuspendedHandler(handler: (() => void) | null) {
	tenantSuspendedHandler = handler;
}

// Best-effort ProblemDetails parse — reads a clone so callers can still access
// `err.response.json()`/`err.response.body` if they need the raw payload.
async function tryReadProblem(
	response: Response,
): Promise<ProblemDetails | undefined> {
	const contentType = response.headers.get("content-type") ?? "";
	if (!contentType.includes("json")) return undefined;
	try {
		const body = await response.clone().json();
		if (body && typeof body === "object") return body as ProblemDetails;
	} catch {
		// non-fatal: body not parseable as JSON
	}
	return undefined;
}

// HA-769: `appFetch` resolves for any status, so a write that reads nothing back reports a
// refusal as success unless it checks. Pass the response through this to turn a non-2xx into
// an HttpRequestError, the same one `fetchJson` throws.
export async function ensureOk(response: Response): Promise<Response> {
	if (response.ok) return response;
	const problem = await tryReadProblem(response);
	const message = problem?.detail ?? problem?.title ?? response.statusText;
	const error = new HttpRequestError(message, response, problem);
	// HA-869: a tenant the caller doesn't belong to is left the same way as a suspended one.
	if (isTenantSuspended(error) || isNotATenantMember(error))
		tenantSuspendedHandler?.();
	throw error;
}

export const NETWORK_ERROR_MESSAGE =
	"Couldn't reach the server. Check your connection and try again.";

/** HA-804: `ApiError.code` for a call that got no response (a network drop or timeout). */
export const NETWORK_ERROR_CODE = "NETWORK";

// HA-769: the `{ success: false }` shape for a failed call, built from whatever was thrown.
// The old catch blocks did `error.response.json()`, which throws a TypeError of its own when
// the request never got a response (a network drop or timeout) or the body was already read.
export async function toApiError(
	e: unknown,
	fallback: string,
): Promise<ApiError> {
	if (e instanceof HttpRequestError) {
		const problem = e.problem ?? (await tryReadProblem(e.response));
		return {
			success: false,
			error: problem?.detail ?? problem?.title ?? (e.message || fallback),
			details: (problem ? toCamel(problem) : {}) as ApiError["details"],
			status: e.response.status,
			code: typeof problem?.code === "string" ? problem.code : undefined,
		};
	}
	if (
		e instanceof TypeError ||
		(e instanceof DOMException &&
			(e.name === "AbortError" || e.name === "TimeoutError"))
	) {
		return {
			success: false,
			error: NETWORK_ERROR_MESSAGE,
			details: {} as ApiError["details"],
			code: NETWORK_ERROR_CODE,
		};
	}
	return {
		success: false,
		error: fallback,
		details: {} as ApiError["details"],
	};
}

export const DEFAULT_FETCH_TIMEOUT_MS = 10_000;

export interface AppFetchOptions extends RequestInit {
	timeoutMs?: number;
}

export async function fetchJson<T>(
	url: URL | RequestInfo,
	opts?: AppFetchOptions | undefined,
): Promise<T> {
	const response = await appFetch(url, {
		...opts,
		headers: {
			"Content-Type": "application/json",
			...opts?.headers,
		},
	});

	await ensureOk(response);

	if (response.status === 204 || response.status === 202) {
		return {} as T;
	}

	const data = await response.json();

	return data as T;
}
export async function appFetch(
	url: URL | RequestInfo,
	opts?: AppFetchOptions | undefined,
) {
	const token = getAuthToken(requestUrl(url));
	const isFormData =
		typeof FormData !== "undefined" && opts?.body instanceof FormData;
	const headers: Record<string, string> = {
		"API-Key": import.meta.env.DATA_API_KEY || "",
		"ngrok-skip-browser-warning": "true",
		...(isFormData
			? {}
			: { "Content-Type": opts?.body ? "application/json" : "" }),
		...(token ? { Authorization: `Bearer ${token}` } : {}),
		...(opts?.headers as Record<string, string> | undefined),
	};
	// Let the browser set multipart/form-data with its own boundary.
	if (isFormData) delete headers["Content-Type"];

	// HA-596: interceptors (product auto-save's If-Match / ETag) see every request first, and
	// before the timeout starts, so a write waiting its turn behind another isn't timed out while
	// it waits.
	const intercepted: InterceptedRequest = {
		url: requestUrl(url),
		method: (
			opts?.method ?? (url instanceof Request ? url.method : "GET")
		).toUpperCase(),
	};
	const before = await runBeforeFetch(intercepted);
	if (before.response) {
		await runAfterFetch(before.ran, intercepted, { response: before.response });
		return before.response;
	}
	Object.assign(headers, before.headers);

	const {
		timeoutMs,
		signal: callerSignal,
		headers: _strip,
		...rest
	} = opts ?? {};
	const timeout = timeoutMs ?? DEFAULT_FETCH_TIMEOUT_MS;
	const timeoutSignal = AbortSignal.timeout(timeout);
	const signal = callerSignal
		? AbortSignal.any([callerSignal, timeoutSignal])
		: timeoutSignal;

	const startedAt =
		typeof performance !== "undefined" ? performance.now() : Date.now();

	try {
		const response = await fetch(url, {
			...rest,
			headers,
			signal,
		});
		await runAfterFetch(before.ran, intercepted, { response });
		return response;
	} catch (err) {
		await runAfterFetch(before.ran, intercepted, { error: err });
		// On AbortError / TimeoutError, fire a tiny beacon so support can correlate
		// "constantly erroring out" reports with concrete URLs and durations even
		// though the original request died mid-stream and produced no server log.
		if (
			err instanceof DOMException &&
			(err.name === "AbortError" || err.name === "TimeoutError")
		) {
			reportAbort({
				url:
					typeof url === "string"
						? url
						: url instanceof URL
							? url.toString()
							: url.url,
				method: (
					rest.method ?? (url instanceof Request ? url.method : "GET")
				).toUpperCase(),
				durationMs:
					(typeof performance !== "undefined"
						? performance.now()
						: Date.now()) - startedAt,
				reason: err.name,
				tenantHint:
					typeof headers["tenant"] === "string" ? headers["tenant"] : undefined,
			});
		}
		throw err;
	}
}

function requestUrl(url: URL | RequestInfo): string {
	return typeof url === "string"
		? url
		: url instanceof URL
			? url.toString()
			: url.url;
}

interface AbortBeaconPayload {
	url: string;
	method: string;
	durationMs: number;
	reason: string;
	tenantHint?: string;
}

function reportAbort(payload: AbortBeaconPayload): void {
	if (typeof window === "undefined") return;
	try {
		const beaconUrl = new URL("/diagnostics/abort", apiUrl).toString();
		const body = JSON.stringify(payload);

		if (
			typeof navigator !== "undefined" &&
			typeof navigator.sendBeacon === "function"
		) {
			const blob = new Blob([body], { type: "application/json" });
			if (navigator.sendBeacon(beaconUrl, blob)) return;
		}

		// Fallback: keepalive fetch — small payload, fire-and-forget, ignore errors.
		void fetch(beaconUrl, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body,
			keepalive: true,
		}).catch(() => {});
	} catch {
		// Beaconing must never throw — if telemetry fails we still want the original
		// abort error to propagate to the caller untouched.
	}
}
